import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import isEmpty from "lodash-es/isEmpty";
import { defineMessages, useIntl } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import {
  Deal,
  EntitiesSupportedByEmailFeature,
  EntityTypesSupportedByEmailFeature,
} from "@mapmycustomers/shared/types/entity";
import { isNotEmpty, isNotEmptyString } from "@mapmycustomers/shared/util/assert";
import { DYNAMIC_VAR_REGEX } from "@mapmycustomers/ui";

import {
  fetchNewEmailAssociatedEntities,
  fetchNewEmailPreview,
  sendEmail,
  uploadFileForNewEmail,
} from "@app/store/email/actions";
import { getUserSettingValue } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import getDynamicVarVariants from "@app/util/fieldModel/util/getDynamicVarVariants";

import CreateEmailContext, {
  EmailCreationContextValue,
  EmailCreationContextValueState,
} from "./CreateEmailContext";

const messages = defineMessages({
  emailBodyLimitReached: {
    id: "email.creation.emailBodyLimitReached",
    defaultMessage: "The email body is too long. Please shorten it to proceed.",
    description: "Email body limit reached",
  },
  filesSizeLimitReached: {
    id: "email.creation.filesSizeLimitReached",
    defaultMessage:
      "Cannot send emails with attachments larger than 24 MB in total. Please remove or compress files and try again.",
    description: "Email attachments limit reached",
  },
});

const ATTACHED_FILES_SIZE_LIMIT = 24 * 1024 * 1024;

type EmailCreationProviderProps = {
  initialValues?: {
    deals?: Deal[];
    recipients?: EntitiesSupportedByEmailFeature[];
    recipientType?: EntityTypesSupportedByEmailFeature;
    template?: EmailTemplate;
  };
} & ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const CreateEmailProvider: React.FC<EmailCreationProviderProps> = ({
  children,
  initialValues,
  onFetchAssociatedEntities,
  onFetchPreview,
  onSend,
  onUploadFile,
  signatureText,
}) => {
  const intl = useIntl();

  const defaultBody = useMemo(
    () => (signatureText.length ? `<div><div></div><div></div>${signatureText}</div>` : ""),
    [signatureText]
  );

  const [state, setState] = useState<EmailCreationContextValueState>(() => {
    const { deals, recipients, recipientType, template } = initialValues ?? {};
    const filteredRecipients = (recipients ?? []).filter(({ email }) => !isEmpty(email));
    return {
      associatedEntities: [],
      attachedFiles: [],
      body: template ? template.body : defaultBody,
      bodyLimitReached: false,
      deals: deals ?? [],
      previewLoading: false,
      previewOpen: false,
      recipients: filteredRecipients,
      recipientType:
        recipients?.[0]?.entity ?? template?.entityType ?? recipientType ?? EntityType.COMPANY,
      sending: false,
      subject: "",
      template: template,
      templateChanged: false,
    };
  });

  const attachedFilesSizeLimitReached = useMemo(() => {
    return (
      ATTACHED_FILES_SIZE_LIMIT <
      state.attachedFiles.reduce<number>(
        (result, attachment) => result + attachment.preparedFile.size,
        0
      )
    );
  }, [state.attachedFiles]);

  const [dynamicVarVariants, recipientMissedFields] = useMemo(() => {
    if (state.recipientType) {
      return getDynamicVarVariants(
        intl,
        state.recipientType,
        state.recipients,
        (Array.from(state.body.matchAll(DYNAMIC_VAR_REGEX)) as RegExpMatchArray[])
          .map(({ groups }) => groups?.fieldName)
          .filter(isNotEmpty)
      );
    }
    return [[], {}];
  }, [state.recipientType, state.body, intl, state.recipients]);

  const updateState = useCallback((newState: Partial<EmailCreationContextValueState>) => {
    setState((state) => ({ ...state, ...newState }));
  }, []);

  const emailFilled = useMemo(
    () => isNotEmptyString(state.body) && isNotEmptyString(state.subject),
    [state.body, state.subject]
  );

  const validationError = useMemo(() => {
    if (attachedFilesSizeLimitReached) {
      return intl.formatMessage(messages.filesSizeLimitReached);
    }

    if (state.bodyLimitReached) {
      return intl.formatMessage(messages.emailBodyLimitReached);
    }
  }, [intl, attachedFilesSizeLimitReached, state.bodyLimitReached]);

  const attachFile = useCallback<EmailCreationContextValue["attachFile"]>(
    (attachingFile) => {
      setState((state) => ({
        ...state,
        attachedFiles: [...state.attachedFiles, { ...attachingFile, uploading: true }],
      }));
      onUploadFile({
        attachedFile: attachingFile,
        callback: (uploadedFile) => {
          setState((state) => ({
            ...state,
            attachedFiles: state.attachedFiles.map((attachment) => {
              if (uploadedFile.id === attachment.id) {
                return { ...uploadedFile, uploading: false };
              }
              return attachment;
            }),
          }));
        },
        failureCallback: () =>
          setState((state) => ({
            ...state,
            attachedFiles: state.attachedFiles.map((attachedFile) => {
              if (attachingFile.id === attachedFile.id) {
                return { ...attachedFile, errored: true, uploading: false };
              }
              return attachedFile;
            }),
          })),
      });
    },
    [onUploadFile]
  );

  const deleteAttachedFile = useCallback<EmailCreationContextValue["deleteAttachedFile"]>(
    (attachedFileId) => {
      setState((state) => ({
        ...state,
        attachedFiles: state.attachedFiles.filter(({ id }) => id !== attachedFileId),
      }));
    },
    []
  );

  const fetchAssociatedEntities = useCallback<EmailCreationContextValue["fetchAssociatedEntities"]>(
    ({ callback, failureCallback, recipients }) => {
      onFetchAssociatedEntities({
        callback: (emailAssociatedEntities) => {
          updateState({ associatedEntities: emailAssociatedEntities });
          callback?.(emailAssociatedEntities);
        },
        deals: state.deals,
        failureCallback,
        recipients,
        recipientType: state.recipientType!,
      });
    },
    [onFetchAssociatedEntities, updateState, state.recipientType, state.deals]
  );

  const createPreview = useCallback<EmailCreationContextValue["createPreview"]>(
    (email) => {
      updateState({ preview: undefined, previewLoading: true });
      onFetchPreview({
        callback: (emailPreview) => updateState({ preview: emailPreview, previewLoading: false }),
        email,
        failureCallback: () => updateState({ previewLoading: false }),
      });
    },
    [onFetchPreview, updateState]
  );

  const send = useCallback<EmailCreationContextValue["send"]>(
    ({ callback, email, failureCallback }) => {
      onSend({
        callback: (email) => {
          callback?.(email);
        },
        email,
        failureCallback: (customFieldErrors) => {
          failureCallback?.(customFieldErrors);
        },
      });
    },
    [onSend]
  );

  const setBody = useCallback<EmailCreationContextValue["setBody"]>(
    (body) => {
      updateState({ body });
    },
    [updateState]
  );

  const setBodyLimitReached = useCallback<EmailCreationContextValue["setBodyLimitReached"]>(
    (bodyLimitReached) => {
      updateState({ bodyLimitReached });
    },
    [updateState]
  );

  const setSending = useCallback<EmailCreationContextValue["setSending"]>(
    (sending) => {
      updateState({ sending });
    },
    [updateState]
  );

  const setSubject = useCallback<EmailCreationContextValue["setSubject"]>(
    (subject) => {
      updateState({ subject });
    },
    [updateState]
  );

  const setRecipients = useCallback<EmailCreationContextValue["setRecipients"]>(
    (recipients) => {
      if (recipients.some((recipient) => recipient.entity !== state.recipientType)) {
        throw new Error("Recipients are not matching with recipient type");
      }

      updateState({ recipients });
    },
    [state.recipientType, updateState]
  );

  const setRecipientType = useCallback<EmailCreationContextValue["setRecipientType"]>(
    (recipientType) => {
      updateState({ recipientType });
    },
    [updateState]
  );

  const setTemplate = useCallback<EmailCreationContextValue["setTemplate"]>(
    (template) => {
      if (template) {
        updateState({
          body: template.body,
          subject: template.subject,
          template,
          templateChanged: false,
        });
      } else {
        updateState({ template });
      }
    },
    [updateState]
  );

  const setTemplateChanged = useCallback<EmailCreationContextValue["setTemplateChanged"]>(
    (templateChanged) => {
      updateState({ templateChanged });
    },
    [updateState]
  );

  const setUnsubscribeLinkIncluded = useCallback<
    EmailCreationContextValue["setUnsubscribeLinkIncluded"]
  >(
    (unsubscribeLinkIncluded: boolean) => {
      updateState({ unsubscribeLinkIncluded });
    },
    [updateState]
  );

  const showPreview = useCallback<EmailCreationContextValue["showPreview"]>(() => {
    updateState({ previewOpen: true });
  }, [updateState]);

  const hidePreview = useCallback<EmailCreationContextValue["hidePreview"]>(() => {
    updateState({ previewOpen: false });
  }, [updateState]);

  const setPreviewRecipientId = useCallback<EmailCreationContextValue["setPreviewRecipientId"]>(
    (previewRecipientId) => {
      updateState({ previewRecipientId });
    },
    [updateState]
  );

  useEffect(() => {
    if (
      state.previewRecipientId &&
      !state.recipients.some(({ id }) => id === state.previewRecipientId)
    ) {
      updateState({ previewRecipientId: undefined });
    }
  }, [state.recipients, state.previewRecipientId, updateState]);

  return (
    <CreateEmailContext.Provider
      value={{
        ...state,
        attachedFilesSizeLimitReached,
        attachFile,
        createPreview,
        defaultBody,
        deleteAttachedFile,
        dynamicVarVariants,
        emailFilled,
        fetchAssociatedEntities,
        hidePreview,
        recipientMissedFields,
        send,
        setBody,
        setBodyLimitReached,
        setPreviewRecipientId,
        setRecipients,
        setRecipientType,
        setSending,
        setSubject,
        setTemplate,
        setTemplateChanged,
        setUnsubscribeLinkIncluded,
        showPreview,
        validationError,
      }}
    >
      {children}
    </CreateEmailContext.Provider>
  );
};

const mapStateToProps = (state: RootState) => ({
  signatureText: getUserSettingValue(state)("bulkEmail").signature
    ? getUserSettingValue(state)("bulkEmail")?.signatureText ?? ""
    : "",
});

const mapDispatchToProps = {
  onFetchAssociatedEntities: fetchNewEmailAssociatedEntities,
  onFetchPreview: fetchNewEmailPreview,
  onSend: sendEmail,
  onUploadFile: uploadFileForNewEmail,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateEmailProvider);
